import {
	GET_NURSE_LICENSES_REQUEST,
	GET_NURSE_LICENSES_SUCCESS,
	GET_NURSE_LICENSES_ERROR,
	GET_NURSE_LICENSE_REQUEST,
	GET_NURSE_LICENSE_SUCCESS,
	GET_NURSE_LICENSE_ERROR,
	CREATE_NURSE_LICENSE_REQUEST,
	CREATE_NURSE_LICENSE_SUCCESS,
	CREATE_NURSE_LICENSE_ERROR,
	EDIT_NURSE_LICENSE_REQUEST,
	EDIT_NURSE_LICENSE_SUCCESS,
	EDIT_NURSE_LICENSE_ERROR,
	DELETE_NURSE_LICENSE_REQUEST,
	DELETE_NURSE_LICENSE_SUCCESS,
	DELETE_NURSE_LICENSE_ERROR
} from "@/actions"

import {
	nurseLicenseService
} from "@/services"

const state = {
	nurseLicenses: [],
	nurseLicense: {},
	loading: false,
	loadedOnce: false
}

import { addNewFilter, isEmpty, decodeLaravelValidationErrors } from "@/utilities";

const allowedLicenseTypes = ['RN', 'APRN'];

const getters = {
	nlLoading: state => state.loading,
	nlLoadedOnce: state => state.loadedOnce,
	nurseLicenses: state => state.nurseLicenses.sort((a, b) => a?.label.localeCompare(b?.label)),
	nurseLicensesFiltered: state => state.nurseLicenses.filter(nl => allowedLicenseTypes.includes(nl?.label)),
	nurseLicenseSelectOptions: (state, getters) => getters.nurseLicenses.filter(nl => allowedLicenseTypes.includes(nl?.label)).map(nl => ({ value: nl.uuid, label: nl.label })),
	nurseLicenseMultiSelectOptions: state => state.nurseLicenses.map(nl => ({ value: nl.uuid, text: nl.label })),
	nurseLicenseByUuid: state => nlUuid => state.nurseLicenses.find(nl => nl.uuid == nlUuid)
}

const actions = {
	async getNurseLicenses({ commit }) {
		commit("GET_NURSE_LICENSES_REQUEST")
		const response = await nurseLicenseService.getNurseLicenses()
		const { status, data } = response
		switch (status) {
			case 200:
				commit("GET_NURSE_LICENSES_SUCCESS", data)
				break
			default:
				commit("GET_NURSE_LICENSES_ERROR")
		}
		return response
	},
	async getNurseLicense({ commit }, params) {
		commit("GET_NURSE_LICENSE_REQUEST")
		const response = await nurseLicenseService.getNurseLicense(params)
		const { status, data } = response
		switch (status) {
			case 200:
				commit("GET_NURSE_LICENSE_SUCCESS", data)
				break
			default:
				commit("GET_NURSE_LICENSE_ERROR")
				break
		}
		return response
	},
	async createNurseLicense({ commit, dispatch }, params) {
		commit("CREATE_NURSE_LICENSE_REQUEST")
		const response = await nurseLicenseService.createNurseLicense(params)
		const { status, data } = response
		switch (status) {
			case 200:
				if (data?.success) {
					commit("CREATE_NURSE_LICENSE_SUCCESS", data);
					dispatch("alert/createAlertSuccess", "Nurse license created", { root: true });
				}

				if (data?.error) {
					commit("CREATE_NURSE_LICENSE_ERROR")
					dispatch("alert/createAlertErrors", data?.errors ? decodeLaravelValidationErrors(data.errors) : "Opps. Something went wrong. Nurse license not created.", { root: true });
				}

				break
			default:
				commit("CREATE_NURSE_LICENSE_ERROR")
		}
		return response
	},
	async editNurseLicense({ commit, dispatch }, params) {
		commit("EDIT_NURSE_LICENSE_REQUEST")
		const response = await nurseLicenseService.editNurseLicense(params)
		const { status, data } = response
		switch (status) {
			case 200:
				if (data?.success) {
					commit("EDIT_NURSE_LICENSE_SUCCESS", data);
					dispatch("alert/createAlertSuccess", "Nurse license updated", { root: true });
				}

				if (data?.error) {
					commit("EDIT_NURSE_LICENSE_ERROR")
					dispatch("alert/createAlertErrors", data?.errors ? decodeLaravelValidationErrors(data.errors) : "Opps. Something went wrong. Nurse license not updated.", { root: true });
				}

				break
			default:
				commit("EDIT_NURSE_LICENSE_ERROR")
		}
		return response
	},
	async deleteNurseLicense({ commit, dispatch }, uuid) {
		commit("DELETE_NURSE_LICENSE_REQUEST")
		const response = await nurseLicenseService.deleteNurseLicense(uuid)
		const { status, data } = response
		switch (status) {
			case 200:
				if (data?.success) {
					const { nurse_license } = data
					commit("DELETE_NURSE_LICENSE_SUCCESS", { ...nurse_license, uuid })
				}

				if (data?.error) {
					commit("DELETE_NURSE_LICENSE_ERROR")
					dispatch("alert/createAlertErrors", data?.errors ? decodeLaravelValidationErrors(data.errors) : "Opps. Something went wrong. Nurse license not deleted.", { root: true });
				}

				break
			default:
				commit("DELETE_NURSE_LICENSE_ERROR")
		}
		return response
	}
}

const mutations = {
	[GET_NURSE_LICENSES_REQUEST]: state => {
		state.loading = true
	},
	[GET_NURSE_LICENSES_SUCCESS]: (state, data) => {
		const nurse_licenses = data.nurse_licenses
		state.nurseLicenses = addNewFilter(state.nurseLicenses, nurse_licenses)
		state.loading = false
	},
	[GET_NURSE_LICENSES_ERROR]: state => {
		state.loading = false
		state.error = true
	},
	[GET_NURSE_LICENSE_REQUEST]: state => {
		state.loading = true
	},
	[GET_NURSE_LICENSE_SUCCESS]: (state, data) => {
		const { nurseLicense } = data
		state.nurseLicenses = addNewFilter(state.nurseLicenses, [nurseLicense])
		state.loading = false
	},
	[GET_NURSE_LICENSE_ERROR]: (state) => {
		state.loading = false
	},

	[CREATE_NURSE_LICENSE_REQUEST]: state => {
		state.loading = true
	},
	[CREATE_NURSE_LICENSE_SUCCESS]: (state, data) => {
		const { nurse_license } = data
		state.nurseLicenses = addNewFilter(state.nurseLicenses, [nurse_license])
		state.loading = false
	},
	[CREATE_NURSE_LICENSE_ERROR]: state => {
		state.loading = false
	},

	[EDIT_NURSE_LICENSE_REQUEST]: state => {
		state.loading = true
	},
	[EDIT_NURSE_LICENSE_SUCCESS]: (state, data) => {
		const { nurse_license } = data
		state.nurseLicenses = addNewFilter(state.nurseLicenses, [nurse_license])
		state.loading = false
	},
	[EDIT_NURSE_LICENSE_ERROR]: state => {
		state.loading = false
	},

	[DELETE_NURSE_LICENSE_REQUEST]: state => {
		state.loading = true
	},
	[DELETE_NURSE_LICENSE_SUCCESS]: (state, nurse_license) => {
		const { deleted_at, uuid } = nurse_license

		if (!isEmpty(deleted_at)) {
			state.nurseLicenses = addNewFilter(state.nurseLicenses, [nurse_license])
		} else {
			state.nurseLicenses = state.nurseLicenses.filter(nl => nl.uuid !== uuid)
		}

		state.loading = false
	},
	[DELETE_NURSE_LICENSE_ERROR]: (state) => {
		state.loading = false
	}

}

export const nurseLicense = {
	namespaced: true,
	state,
	getters,
	actions,
	mutations
}
